import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Ticket, Plus, Trash, ToggleLeft, ToggleRight, Copy, Sparkle, Crown, Percent, CalendarBlank } from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  listPromoCodes, createPromoCode, updatePromoCode, deletePromoCode, listPromoRedemptions,
} from '../lib/api';

// Keep in sync with backend PLAN_CATALOG (tenancy.py). 'pro' is an alias for 'core'.
const PLAN_KEYS = ['starter', 'grow', 'core', 'scale', 'business', 'enterprise'];

function CodeEditor({ open, onClose, onSaved }) {
  const blank = {
    code: '',
    kind: 'percent',
    percent_off: 20,
    cycles: 1,
    plan_key: 'business',
    max_redemptions: '',
    expires_at: '',
    notes: '',
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(blank); /* eslint-disable-next-line */ }, [open]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        kind: form.kind,
        notes: form.notes || null,
        max_redemptions: form.max_redemptions === '' ? null : parseInt(form.max_redemptions),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      if (form.code.trim()) body.code = form.code.trim().toUpperCase();
      if (form.kind === 'percent') {
        body.percent_off = parseInt(form.percent_off);
        body.cycles = parseInt(form.cycles);
      } else {
        body.plan_key = form.plan_key;
      }
      await createPromoCode(body);
      toast.success('Code created');
      onSaved?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="promo-dialog">
        <DialogHeader>
          <DialogTitle>New discount code</DialogTitle>
          <DialogDescription>Codes are SaaS-wide. Apply at signup time.</DialogDescription>
        </DialogHeader>

        {/* Kind selector — pill toggle so the choice is always visible */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => upd('kind', 'percent')}
            data-testid="promo-kind-percent"
            className={`p-3 border-2 text-left transition-all ${form.kind === 'percent' ? 'border-brand bg-brand/5' : 'border-border hover:border-foreground/40'}`}
          >
            <div className="flex items-center gap-2"><Percent size={16} weight="fill" className="text-brand" /><div className="font-bold text-sm">Percentage off</div></div>
            <div className="text-xs text-muted-foreground mt-1">e.g. 20% off for 3 billing cycles</div>
          </button>
          <button
            type="button"
            onClick={() => upd('kind', 'free_forever')}
            data-testid="promo-kind-free"
            className={`p-3 border-2 text-left transition-all ${form.kind === 'free_forever' ? 'border-amber-500 bg-amber-50' : 'border-border hover:border-foreground/40'}`}
          >
            <div className="flex items-center gap-2"><Crown size={16} weight="fill" className="text-amber-500" /><div className="font-bold text-sm">Free forever</div></div>
            <div className="text-xs text-muted-foreground mt-1">Unlock a plan permanently — no Stripe checkout</div>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="col-span-2">
            <Label>Code <span className="text-xs text-muted-foreground">(blank = auto)</span></Label>
            <Input
              data-testid="promo-code"
              value={form.code}
              onChange={(e) => upd('code', e.target.value.toUpperCase())}
              placeholder="LAUNCH20"
              className="font-mono"
            />
          </div>

          {form.kind === 'percent' && (
            <>
              <div>
                <Label>Percent off (%)</Label>
                <Input data-testid="promo-percent" type="number" min={1} max={100} value={form.percent_off} onChange={(e) => upd('percent_off', e.target.value)} />
              </div>
              <div>
                <Label>Billing cycles</Label>
                <Input data-testid="promo-cycles" type="number" min={1} max={60} value={form.cycles} onChange={(e) => upd('cycles', e.target.value)} />
              </div>
            </>
          )}

          {form.kind === 'free_forever' && (
            <div className="col-span-2">
              <Label>Plan unlocked</Label>
              <Select value={form.plan_key} onValueChange={(v) => upd('plan_key', v)}>
                <SelectTrigger data-testid="promo-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLAN_KEYS.map((p) => <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>Max redemptions <span className="text-xs text-muted-foreground">(blank = unlimited)</span></Label>
            <Input data-testid="promo-max" type="number" min={1} value={form.max_redemptions} onChange={(e) => upd('max_redemptions', e.target.value)} placeholder="∞" />
          </div>
          <div>
            <Label>Expires at <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input data-testid="promo-expires" type="datetime-local" value={form.expires_at} onChange={(e) => upd('expires_at', e.target.value)} />
          </div>
          <div className="col-span-2">
            <Label>Notes (internal)</Label>
            <Textarea data-testid="promo-notes" rows={2} value={form.notes} onChange={(e) => upd('notes', e.target.value)} placeholder="Who is this for? Conversation context, etc." />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="promo-save" onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create code'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditExpiryDialog({ codeRow, onClose, onSaved }) {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!codeRow) return;
    // Pre-fill with existing expiry (in local time so the input renders sanely)
    if (codeRow.expires_at) {
      const d = new Date(codeRow.expires_at);
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString().slice(0, 16);
      setVal(local);
    } else {
      setVal('');
    }
  }, [codeRow]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { expires_at: val ? new Date(val).toISOString() : null };
      await updatePromoCode(codeRow.id, body);
      toast.success(val ? 'Expiry updated' : 'Expiry cleared');
      onSaved?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!codeRow} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="promo-expiry-dialog">
        <DialogHeader>
          <DialogTitle>Edit expiry for <code className="font-mono">{codeRow?.code}</code></DialogTitle>
          <DialogDescription>
            Pick a future date/time, or clear it to remove the expiry entirely.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>Expires at</Label>
          <Input
            data-testid="promo-expiry-input"
            type="datetime-local"
            value={val}
            onChange={(e) => setVal(e.target.value)}
          />
          {val && new Date(val) < new Date() && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-2 py-1">
              ⚠ That&apos;s in the past — the code will be rejected.
            </div>
          )}
          <Button variant="outline" size="sm" onClick={() => setVal('')} data-testid="promo-expiry-clear">
            Clear expiry (no expiry)
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="promo-expiry-save">
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RedemptionsDialog({ codeRow, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!codeRow) return;
    listPromoRedemptions(codeRow.id).then((r) => setRows(r.data?.items || []));
  }, [codeRow]);
  return (
    <Dialog open={!!codeRow} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="promo-redemptions">
        <DialogHeader>
          <DialogTitle>Redemptions for <code className="font-mono">{codeRow?.code}</code></DialogTitle>
          <DialogDescription>{codeRow?.redemption_count || 0} customers have used this code so far.</DialogDescription>
        </DialogHeader>
        {!rows ? (
          <div className="text-sm text-muted-foreground p-4 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 text-center">No redemptions yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest">
              <tr><th className="text-left p-2">When</th><th className="text-left p-2">Owner email</th><th className="text-left p-2">Applied</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`redemption-row-${r.id}`}>
                  <td className="p-2 font-mono text-xs">{(r.redeemed_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="p-2 font-mono text-xs">{r.owner_email}</td>
                  <td className="p-2 text-xs">{r.applied?.label || r.kind}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PromoCodes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [seeRedemptions, setSeeRedemptions] = useState(null);
  const [editExpiry, setEditExpiry] = useState(null);

  const load = async () => {
    try { const r = await listPromoCodes(); setItems(r.data?.items || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed (superadmin only)'); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (row) => {
    try { await updatePromoCode(row.id, { active: !row.active }); load(); }
    catch (e) { toast.error('Toggle failed'); }
  };
  const doDelete = async () => {
    try { await deletePromoCode(pendingDelete.id); toast.success('Deleted'); setPendingDelete(null); load(); }
    catch (e) { toast.error('Delete failed'); }
  };
  const copy = (code) => { navigator.clipboard.writeText(code); toast.success(`${code} copied`); };

  const totals = useMemo(() => ({
    active: items.filter((r) => r.active).length,
    redemptions: items.reduce((s, r) => s + (r.redemption_count || 0), 0),
  }), [items]);

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="promo-codes-page">
        <div className="flex items-center gap-3 mb-1">
          <Ticket size={28} weight="duotone" className="text-brand" />
          <h1 className="text-2xl font-bold tracking-tight">Discount Codes</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Generate codes for first-month discounts or full free-forever access. Codes are validated + redeemed at signup.
        </p>

        <div className="flex items-center gap-3 mb-4">
          <Badge variant="outline" data-testid="promo-active-count">{totals.active} active</Badge>
          <Badge variant="outline" data-testid="promo-redemption-count">{totals.redemptions} total redemptions</Badge>
          <div className="ml-auto" />
          <Button data-testid="promo-new" onClick={() => setOpen(true)}><Plus size={14} className="mr-1" />New code</Button>
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground p-8 border border-dashed border-border text-center">
            No codes yet. Create one above — handy for one-off deals, partner accounts, or your own employees.
          </div>
        ) : (
          <div className="border border-border overflow-x-auto" data-testid="promo-list">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left p-2">Code</th>
                  <th className="text-left p-2">Kind</th>
                  <th className="text-left p-2">Discount</th>
                  <th className="text-left p-2">Used</th>
                  <th className="text-left p-2">Limit</th>
                  <th className="text-left p-2">Expires</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const isExpired = r.expires_at && new Date(r.expires_at) < new Date();
                  const isExhausted = r.max_redemptions != null && (r.redemption_count || 0) >= r.max_redemptions;
                  return (
                  <tr key={r.id} className="border-t border-border" data-testid={`promo-row-${r.id}`}>
                    <td className="p-2"><code className="font-mono font-bold">{r.code}</code></td>
                    <td className="p-2">
                      {r.kind === 'percent'
                        ? <Badge className="bg-brand">Percent</Badge>
                        : <Badge className="bg-amber-500">Free forever</Badge>}
                    </td>
                    <td className="p-2 text-xs">
                      {r.kind === 'percent'
                        ? `${r.percent_off}% off · ${r.cycles} cycle${r.cycles !== 1 ? 's' : ''}`
                        : `${(r.plan_key || 'business').toUpperCase()} unlocked`}
                    </td>
                    <td className="p-2 tabular-nums">
                      <button onClick={() => setSeeRedemptions(r)} className="hover:underline" data-testid={`promo-redemptions-${r.id}`}>
                        {r.redemption_count || 0}
                      </button>
                    </td>
                    <td className="p-2 text-xs">{r.max_redemptions ?? '∞'}</td>
                    <td className="p-2 text-xs">
                      {r.expires_at ? (
                        <span className={isExpired ? 'text-rose-700 font-bold' : ''}>
                          {new Date(r.expires_at).toLocaleDateString()}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="p-2">
                      {!r.active
                        ? <Badge variant="outline" data-testid={`promo-status-off-${r.id}`}>off</Badge>
                        : isExpired
                          ? <Badge className="bg-rose-600" data-testid={`promo-status-expired-${r.id}`}>EXPIRED</Badge>
                          : isExhausted
                            ? <Badge className="bg-zinc-600" data-testid={`promo-status-exhausted-${r.id}`}>used up</Badge>
                            : <Badge className="bg-emerald-600" data-testid={`promo-status-on-${r.id}`}>active</Badge>}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" data-testid={`promo-copy-${r.id}`} onClick={() => copy(r.code)} title="Copy code"><Copy size={14} /></Button>
                      <Button size="sm" variant="ghost" data-testid={`promo-edit-expiry-${r.id}`} onClick={() => setEditExpiry(r)} title="Edit expiry"><CalendarBlank size={14} /></Button>
                      <Button size="sm" variant="ghost" data-testid={`promo-toggle-${r.id}`} onClick={() => toggleActive(r)} title={r.active ? 'Disable' : 'Enable'}>
                        {r.active ? <ToggleRight size={16} className="text-emerald-600" weight="fill" /> : <ToggleLeft size={16} className="text-zinc-400" />}
                      </Button>
                      <Button size="sm" variant="ghost" data-testid={`promo-delete-${r.id}`} onClick={() => setPendingDelete(r)}><Trash size={14} className="text-rose-600" /></Button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <CodeEditor open={open} onClose={() => setOpen(false)} onSaved={load} />
        <RedemptionsDialog codeRow={seeRedemptions} onClose={() => setSeeRedemptions(null)} />
        <EditExpiryDialog codeRow={editExpiry} onClose={() => setEditExpiry(null)} onSaved={load} />

        <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
          <AlertDialogContent data-testid="promo-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete code?</AlertDialogTitle>
              <AlertDialogDescription>
                <code className="font-mono font-bold">{pendingDelete?.code}</code> will be permanently removed.
                Existing redemptions stay in the audit log; companies that already redeemed it keep their discount.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={doDelete} data-testid="promo-delete-confirm">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
